<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";

  .file-upload-form {
    display: flex;
    flex-direction: column;
    border-top: 1px solid colors.get(interfaceSwGrey_10);
    gap: utils.in_rem(24px);
    padding: utils.in_rem(24px);

    .form-actions {
      display: flex;
      flex-direction: column;
      gap: utils.in_rem(12px);
    }
  }
</style>

<script>
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import FileUpload from "@sourcewhale/shared/svelte-design-system/components/molecules/FileUpload/FileUpload.svelte";
  import UploadProgress from "@sourcewhale/shared/svelte-design-system/components/molecules/UploadProgress/UploadProgress.svelte";
  import { showToast } from "@sourcewhale/core";
  import { saveCandidate } from "@sourcewhale/shared/src/svelte/api/candidates";
  import { saveFile } from "../../../../../api/files";
  import { fileToDataUrl } from "../../../../../utils/files.js";
  import { candidateStore } from "../../stores";

  export let closeModal;

  let files = [];
  let isSubmitting = false;
  let errorMessage = "";
  let progress = 0;

  $: isValid = files.length > 0;

  const handleFilesSelected = ({ files: selectedFiles }) => {
    files = selectedFiles;
  };

  // Process a single file
  const processFile = async (file, index, totalFiles) => {
    // Update progress based on completed files
    progress = Math.round((index / totalFiles) * 100);

    // Read file as data URL
    const data = await fileToDataUrl(file);

    // Save file using the new endpoint
    const res = await saveFile(data, file.name);

    return res.fileId;
  };

  const uploadFiles = async () => {
    if (!isValid || isSubmitting) return;

    isSubmitting = true;
    errorMessage = "";
    progress = 0;

    try {
      const { candidateId } = $candidateStore;
      if (!candidateId) {
        throw new Error("No candidate ID found");
      }

      const totalFiles = files.length;
      const fileIds = [];
      const filePromises = [];

      // Create an array of promises for file processing
      files.forEach((file, index) => {
        const promise = processFile(file, index, totalFiles)
          .then((fileId) => {
            fileIds.push(fileId);
            // Update progress after each file is processed
            progress = Math.round(((index + 1) / totalFiles) * 100);
          })
          .catch(() => {
            showToast({
              title: "Error",
              subtitle: `Failed to upload file: ${file.name}`,
              type: "danger",
            });
          });

        filePromises.push(promise);
      });

      // Wait for all files to be processed
      await Promise.all(filePromises);

      // Set progress to 100% when all files are processed
      progress = 100;

      if (fileIds.length > 0) {
        // Update candidate with new file IDs
        const currentFiles = Array.isArray($candidateStore.fileIds)
          ? [...$candidateStore.fileIds]
          : [];

        const updatedFiles = [...currentFiles, ...fileIds];

        await saveCandidate({
          candidateId: candidateId,
          fileIds: updatedFiles,
        });

        showToast({
          title: "Success",
          subtitle: `${fileIds.length} file${fileIds.length === 1 ? "" : "s"} uploaded successfully.`,
          type: "success",
        });

        closeModal();
      } else {
        errorMessage = "No files were successfully uploaded.";
        isSubmitting = false;
      }
    } catch {
      errorMessage = "Failed to upload files. Please try again.";
      isSubmitting = false;
    }
  };
</script>

<form on:submit|preventDefault={uploadFiles} class="file-upload-form">
  <FileUpload
    maxFiles={Infinity}
    maxFileSize={128 * 1024 * 1024}
    uploadText="Drop your files here or click to browse"
    uploadSubtext="Upload multiple files at once"
    onFilesSelected={handleFilesSelected}
    isUploading={isSubmitting}
  />

  {#if isSubmitting}
    <UploadProgress {progress} {errorMessage} />
  {/if}

  <div class="form-actions">
    <Button
      type="submit"
      class="full-width {!isValid || isSubmitting ? 'disabled' : ''}"
      leadingIcon="fa-regular fa-cloud-arrow-up"
      disabled={!isValid || isSubmitting}
    >
      {isSubmitting ? "Uploading..." : "Upload Files"}
    </Button>

    <Button
      type="button"
      class="secondary"
      onclick={closeModal}
      disabled={isSubmitting}
    >
      Cancel
    </Button>
  </div>
</form>
